/**
 * 사용자 관리 페이지 (SaaS)
 * - 목록: 편집 / 구독 이력
 * - 편집 다이얼로그: 속성 수정 + 구독 이력 + 삭제
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Stack,
  Tooltip,
  Menu,
} from '@mui/material';
import {
  Edit as EditIcon,
  ReceiptLong as HistoryIcon,
  PersonAdd as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { usersAPI } from '@/services/api';

async function downloadUsersExport(blob, format) {
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `koai-users-${stamp}.${format}`;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const PLAN_LABEL_CHIPS = ['인플루언서 보상', '파트너 제공', '내부 테스트', '베타 테스터'];

function toDateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDurationToToday({ months = 0, years = 0 } = {}) {
  const d = new Date();
  if (years) d.setFullYear(d.getFullYear() + years);
  if (months) d.setMonth(d.getMonth() + months);
  return toDateInputValue(d);
}

const PLAN_DURATION_PRESETS = [
  { label: '3개월', months: 3 },
  { label: '6개월', months: 6 },
  { label: '9개월', months: 9 },
  { label: '1년', years: 1 },
  { label: '2년', years: 2 },
  { label: '3년', years: 3 },
];

function buildPlanPreview({ role, plan, planLabel, planExpiresAt }) {
  if (role === 'admin') return '플랫폼 관리자';
  if (plan !== 'paid') return '무료';
  const label = (planLabel || '').trim();
  if (!label) return '스탠다드';
  const dateStr = toDateInputValue(planExpiresAt);
  if (!dateStr) return `스탠다드 (${label})`;
  return `스탠다드 (${label} · ~${dateStr})`;
}

function planLabel(row) {
  if (row.isAdmin || row.role === 'admin') return '플랫폼 관리자';
  if (row.planNamePreview) return row.planNamePreview;
  if (row.planName) return row.planName;
  if (row.plan === 'paid' || row.plan === 'standard') {
    return buildPlanPreview({
      role: row.role,
      plan: 'paid',
      planLabel: row.planLabel,
      planExpiresAt: row.planExpiresAt,
    });
  }
  return '무료';
}

function planChipColor(row) {
  if (row.isAdmin || row.role === 'admin') return 'secondary';
  if (row.plan === 'paid' || row.plan === 'standard') return 'primary';
  return 'default';
}

function emptyGrantFields() {
  return {
    planLabel: '',
    planExpiresAt: addDurationToToday({ years: 2 }),
    planValueUsd: '120',
    planNote: '',
  };
}

function formatHistoryDetails(details) {
  if (!details) return '-';
  if (typeof details === 'string') return details;
  const parts = [];
  if (details.label) parts.push(`표시: ${details.label}`);
  if (details.expiresAt) parts.push(`만료: ${toDateInputValue(details.expiresAt)}`);
  if (details.valueUsd != null) parts.push(`$${details.valueUsd}`);
  if (details.from != null || details.to != null) {
    parts.push(`${details.from ?? '-'} → ${details.to ?? '-'}`);
  }
  if (details.by) parts.push(`by ${details.by}`);
  if (details.reason) parts.push(details.reason);
  if (parts.length) return parts.join(' · ');
  try {
    return JSON.stringify(details);
  } catch {
    return '-';
  }
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatDateTime(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function UsersPage() {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({
    email: '',
    password: '',
    name: '',
    status: 'active',
    plan: 'free',
    role: 'user',
    ...emptyGrantFields(),
  });

  // 편집
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    name: '',
    status: 'active',
    plan: 'free',
    role: 'user',
    ...emptyGrantFields(),
  });
  const [history, setHistory] = useState([]);

  const resetAddForm = () => {
    setAddForm({
      email: '',
      password: '',
      name: '',
      status: 'active',
      plan: 'free',
      role: 'user',
      ...emptyGrantFields(),
    });
  };

  const renderGrantFields = (formState, setFormState) => {
    if (formState.role === 'admin' || formState.plan !== 'paid') return null;
    const preview = buildPlanPreview(formState);
    return (
      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          이용권 지급 (보상·프로모션)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          표시 이름과 종료일은 필수입니다. 미리보기: <strong>{preview}</strong>
        </Typography>
        <Stack spacing={1.5}>
          <TextField
            label="표시 이름"
            value={formState.planLabel}
            onChange={(e) => setFormState((f) => ({ ...f, planLabel: e.target.value }))}
            fullWidth
            size="small"
            required
            placeholder="예: 인플루언서 보상"
            helperText="마이페이지에 「스탠다드 (표시 이름 · 날짜까지)」로 표시됩니다."
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {PLAN_LABEL_CHIPS.map((chip) => (
              <Chip
                key={chip}
                label={chip}
                size="small"
                variant={formState.planLabel === chip ? 'filled' : 'outlined'}
                color={formState.planLabel === chip ? 'primary' : 'default'}
                onClick={() => setFormState((f) => ({ ...f, planLabel: chip }))}
              />
            ))}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="종료일"
              type="date"
              value={formState.planExpiresAt}
              onChange={(e) => setFormState((f) => ({ ...f, planExpiresAt: e.target.value }))}
              fullWidth
              size="small"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="가치 ($)"
              type="number"
              value={formState.planValueUsd}
              onChange={(e) => setFormState((f) => ({ ...f, planValueUsd: e.target.value }))}
              fullWidth
              size="small"
              inputProps={{ min: 0, step: '0.01' }}
              placeholder="120"
            />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {PLAN_DURATION_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                size="small"
                variant="outlined"
                onClick={() => setFormState((f) => ({
                  ...f,
                  planExpiresAt: addDurationToToday({
                    months: preset.months || 0,
                    years: preset.years || 0,
                  }),
                }))}
              >
                {preset.label}
              </Button>
            ))}
          </Stack>
          <TextField
            label="내부 메모 (선택)"
            value={formState.planNote}
            onChange={(e) => setFormState((f) => ({ ...f, planNote: e.target.value }))}
            fullWidth
            size="small"
            placeholder="캠페인명, 담당자 등"
          />
        </Stack>
      </Box>
    );
  };

  const handleExport = async (format) => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const params = { format };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await usersAPI.export(params);
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed.error || '내보내기에 실패했습니다.');
      }
      await downloadUsersExport(response.data, format);
      setSnackbar({
        open: true,
        message: format === 'csv' ? 'CSV 파일을 저장했습니다.' : 'Excel 파일을 저장했습니다.',
        severity: 'success',
      });
    } catch (error) {
      let message = '사용자 목록 내보내기에 실패했습니다.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error) message = parsed.error;
        } catch (_) { /* ignore */ }
      } else if (error.message) {
        message = error.message;
      }
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // 구독 이력만 보는 다이얼로그
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyOnly, setHistoryOnly] = useState([]);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll({ limit: 100 });
      setUsers(response.data.data.users || []);
    } catch (error) {
      console.error('Load users error:', error);
      setSnackbar({
        open: true,
        message: '사용자 목록을 불러오는데 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async (row) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditUser(row);
    setHistory([]);
    try {
      const [detailRes, histRes] = await Promise.all([
        usersAPI.getById(row.id),
        usersAPI.getSubscriptionHistory(row.id),
      ]);
      const detail = detailRes.data?.data?.user || row;
      setEditUser(detail);
      const defaults = emptyGrantFields();
      setForm({
        name: detail.name || '',
        status: detail.status || 'active',
        plan: detail.plan === 'paid' ? 'paid' : 'free',
        role: detail.role === 'admin' ? 'admin' : 'user',
        planLabel: detail.planLabel || defaults.planLabel,
        planExpiresAt: toDateInputValue(detail.planExpiresAt) || defaults.planExpiresAt,
        planValueUsd:
          detail.planValueUsd != null && detail.planValueUsd !== ''
            ? String(detail.planValueUsd)
            : defaults.planValueUsd,
        planNote: detail.planNote || '',
      });
      setHistory(histRes.data?.data?.history || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '사용자 정보를 불러오지 못했습니다.',
        severity: 'error',
      });
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const openHistory = async (row) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryUser(row);
    setHistoryOnly([]);
    try {
      const histRes = await usersAPI.getSubscriptionHistory(row.id);
      setHistoryUser(histRes.data?.data?.user || row);
      setHistoryOnly(histRes.data?.data?.history || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '구독 이력을 불러오지 못했습니다.',
        severity: 'error',
      });
      setHistoryOpen(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editUser) return;
    if (form.role !== 'admin' && form.plan === 'paid') {
      if (!form.planLabel.trim() || form.planLabel.trim().length < 2) {
        setSnackbar({ open: true, message: '유료 이용권의 표시 이름을 2자 이상 입력하세요.', severity: 'warning' });
        return;
      }
      if (!form.planExpiresAt) {
        setSnackbar({ open: true, message: '유료 이용권의 종료일을 선택하세요.', severity: 'warning' });
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        status: form.status,
        role: form.role,
      };
      // admin 역할이면 plan은 서버에서 한도 무시 — 일반 유저일 때만 plan 전송
      if (form.role !== 'admin') {
        payload.plan = form.plan;
        if (form.plan === 'paid') {
          payload.planLabel = form.planLabel.trim();
          payload.planExpiresAt = form.planExpiresAt;
          payload.planValueUsd = form.planValueUsd === '' ? null : Number(form.planValueUsd);
          payload.planNote = form.planNote.trim() || null;
        }
      }
      await usersAPI.update(editUser.id, payload);
      setSnackbar({ open: true, message: '저장되었습니다.', severity: 'success' });
      setEditOpen(false);
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '저장에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editUser) return;
    if (editUser.id === user?.id) {
      setSnackbar({ open: true, message: '자기 자신은 삭제할 수 없습니다.', severity: 'warning' });
      return;
    }
    if (!window.confirm(`"${editUser.email}" 사용자를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }
    try {
      await usersAPI.delete(editUser.id);
      setSnackbar({ open: true, message: '사용자가 삭제되었습니다.', severity: 'success' });
      setEditOpen(false);
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '삭제에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  const openAdd = () => {
    resetAddForm();
    setAddDialogOpen(true);
  };

  const handleAddUser = async () => {
    const email = addForm.email.trim();
    const password = addForm.password;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setSnackbar({ open: true, message: '올바른 이메일 형식을 입력해주세요.', severity: 'warning' });
      return;
    }
    if (!password || password.length < 6) {
      setSnackbar({ open: true, message: '비밀번호는 6자 이상이어야 합니다.', severity: 'warning' });
      return;
    }

    if (addForm.role !== 'admin' && addForm.plan === 'paid') {
      if (!addForm.planLabel.trim() || addForm.planLabel.trim().length < 2) {
        setSnackbar({ open: true, message: '유료 이용권의 표시 이름을 2자 이상 입력하세요.', severity: 'warning' });
        return;
      }
      if (!addForm.planExpiresAt) {
        setSnackbar({ open: true, message: '유료 이용권의 종료일을 선택하세요.', severity: 'warning' });
        return;
      }
    }

    setAdding(true);
    try {
      const payload = {
        email,
        password,
        name: addForm.name.trim() || undefined,
        status: addForm.status,
        role: addForm.role,
      };
      if (addForm.role !== 'admin') {
        payload.plan = addForm.plan;
        if (addForm.plan === 'paid') {
          payload.planLabel = addForm.planLabel.trim();
          payload.planExpiresAt = addForm.planExpiresAt;
          payload.planValueUsd = addForm.planValueUsd === '' ? null : Number(addForm.planValueUsd);
          payload.planNote = addForm.planNote.trim() || null;
        }
      }
      await usersAPI.create(payload);
      setSnackbar({ open: true, message: '사용자가 추가되었습니다.', severity: 'success' });
      setAddDialogOpen(false);
      resetAddForm();
      loadUsers();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '사용자 추가에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setAdding(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    (u.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const renderHistoryTable = (items) => {
    if (!items.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          구독 이력이 없습니다.
        </Typography>
      );
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>일시</TableCell>
            <TableCell>이벤트</TableCell>
            <TableCell>상세</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((h) => (
            <TableRow key={h.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(h.createdAt)}</TableCell>
              <TableCell>
                <Chip label={h.actionLabel || h.action} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                  {formatHistoryDetails(h.details)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight="bold">
          사용자 관리
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={18} /> : <ExportIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={exporting || loading}
          >
            내보내기
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => handleExport('xlsx')}>Excel (.xlsx)</MenuItem>
            <MenuItem onClick={() => handleExport('csv')}>CSV (.csv)</MenuItem>
          </Menu>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            사용자 추가
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="이메일로 검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>이메일</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>구독</TableCell>
                <TableCell>가입일</TableCell>
                <TableCell>최근 로그인</TableCell>
                <TableCell align="center">편집</TableCell>
                <TableCell align="center">구독 이력</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchTerm ? '검색 결과가 없습니다.' : '사용자가 없습니다.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {row.email}
                          {row.id === user?.id && (
                            <Chip label="나" size="small" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            row.status === 'active'
                              ? '활성'
                              : row.status === 'pending'
                                ? '대기'
                                : '비활성'
                          }
                          color={
                            row.status === 'active'
                              ? 'success'
                              : row.status === 'pending'
                                ? 'warning'
                                : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={planLabel(row)}
                          color={planChipColor(row)}
                          size="small"
                          variant={
                            row.plan === 'paid' || row.role === 'admin' ? 'filled' : 'outlined'
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(row.createdAt)}</TableCell>
                      <TableCell>{formatDate(row.lastLoginAt)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="편집">
                          <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="구독 이력 보기">
                          <IconButton size="small" onClick={() => openHistory(row)}>
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="페이지당 행:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}명`}
          />
        </TableContainer>
      )}

      {/* 편집 다이얼로그 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>사용자 편집</DialogTitle>
        <DialogContent dividers>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  기본 정보
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="이메일"
                    value={editUser?.email || ''}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="이름"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>상태</InputLabel>
                      <Select
                        label="상태"
                        value={form.status}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      >
                        <MenuItem value="active">활성</MenuItem>
                        <MenuItem value="pending">대기</MenuItem>
                        <MenuItem value="inactive">비활성</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>역할</InputLabel>
                      <Select
                        label="역할"
                        value={form.role}
                        onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                      >
                        <MenuItem value="user">일반 사용자</MenuItem>
                        <MenuItem value="admin">플랫폼 관리자</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={form.role === 'admin'}>
                      <InputLabel>요금제(plan)</InputLabel>
                      <Select
                        label="요금제(plan)"
                        value={form.plan}
                        onChange={(e) => {
                          const plan = e.target.value;
                          setForm((f) => ({
                            ...f,
                            plan,
                            ...(plan === 'paid' && !f.planLabel
                              ? { planLabel: '인플루언서 보상', planExpiresAt: f.planExpiresAt || addDurationToToday({ years: 2 }), planValueUsd: f.planValueUsd || '120' }
                              : {}),
                          }));
                        }}
                      >
                        <MenuItem value="free">무료 (free)</MenuItem>
                        <MenuItem value="paid">유료/스탠다드 (paid)</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  {form.role === 'admin' && (
                    <Alert severity="info" sx={{ py: 0 }}>
                      플랫폼 관리자는 요금제 한도의 영향을 받지 않습니다.
                    </Alert>
                  )}
                  {renderGrantFields(form, setForm)}
                  {editUser?.planSource === 'polar' && form.plan === 'paid' && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      현재 Polar 결제 구독입니다. 저장하면 보상 이용권(comp)으로 덮어씁니다.
                    </Alert>
                  )}
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      가입일: {formatDate(editUser?.createdAt || editUser?.created_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      최근 로그인:{' '}
                      {formatDate(editUser?.lastLoginAt || editUser?.last_login_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {editUser?.id}
                      {editUser?.planSource ? ` · source: ${editUser.planSource}` : ''}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  구독 이력 관리
                </Typography>
                {renderHistoryTable(history)}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={
              editLoading || saving || !editUser || editUser.id === user?.id
            }
          >
            삭제
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditOpen(false)} disabled={saving}>
              취소
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={editLoading || saving}
            >
              {saving ? '저장 중...' : '수정 저장'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* 구독 이력 전용 다이얼로그 */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          구독 이력
          {historyUser?.email ? (
            <Typography variant="body2" color="text.secondary">
              {historyUser.email}
              {historyUser.planName ? ` · ${historyUser.planName}` : ''}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            renderHistoryTable(historyOnly)
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>닫기</Button>
          <Button
            variant="outlined"
            onClick={() => {
              setHistoryOpen(false);
              if (historyUser?.id) {
                const row = users.find((u) => u.id === historyUser.id) || historyUser;
                openEdit(row);
              }
            }}
          >
            편집으로 이동
          </Button>
        </DialogActions>
      </Dialog>

      {/* 사용자 추가 — 편집과 동일한 속성 + 초기 비밀번호 */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !adding && setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>사용자 추가</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 0.5 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                기본 정보
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="이메일"
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  fullWidth
                  size="small"
                  required
                  placeholder="user@example.com"
                />
                <TextField
                  label="초기 비밀번호"
                  type="password"
                  value={addForm.password}
                  onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                  fullWidth
                  size="small"
                  required
                  helperText="6자 이상. 사용자에게 전달 후 변경을 권장합니다."
                />
                <TextField
                  label="이름"
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  fullWidth
                  size="small"
                  placeholder="비우면 이메일 앞부분을 사용합니다"
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>상태</InputLabel>
                    <Select
                      label="상태"
                      value={addForm.status}
                      onChange={(e) => setAddForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      <MenuItem value="active">활성</MenuItem>
                      <MenuItem value="pending">대기</MenuItem>
                      <MenuItem value="inactive">비활성</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>역할</InputLabel>
                    <Select
                      label="역할"
                      value={addForm.role}
                      onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                    >
                      <MenuItem value="user">일반 사용자</MenuItem>
                      <MenuItem value="admin">플랫폼 관리자</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" disabled={addForm.role === 'admin'}>
                    <InputLabel>요금제(plan)</InputLabel>
                    <Select
                      label="요금제(plan)"
                      value={addForm.plan}
                      onChange={(e) => {
                        const plan = e.target.value;
                        setAddForm((f) => ({
                          ...f,
                          plan,
                          ...(plan === 'paid' && !f.planLabel
                            ? { planLabel: '인플루언서 보상', planExpiresAt: f.planExpiresAt || addDurationToToday({ years: 2 }), planValueUsd: f.planValueUsd || '120' }
                            : {}),
                        }));
                      }}
                    >
                      <MenuItem value="free">무료 (free)</MenuItem>
                      <MenuItem value="paid">유료/스탠다드 (paid)</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                {addForm.role === 'admin' && (
                  <Alert severity="info" sx={{ py: 0 }}>
                    플랫폼 관리자는 요금제 한도의 영향을 받지 않습니다.
                  </Alert>
                )}
                {renderGrantFields(addForm, setAddForm)}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                구독 이력 관리
              </Typography>
              <Typography variant="body2" color="text.secondary">
                신규 사용자는 구독 이력이 없습니다. 유료 이용권으로 생성하면 「이용권 지급」
                이력이 기록되며, 추가 후 편집에서 확인할 수 있습니다.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={adding}>
            취소
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            disabled={adding || !addForm.email.trim() || !addForm.password}
          >
            {adding ? '추가 중...' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
